import { Box } from '@mui/material';
import React from 'react';
import { useOutletContext, useParams } from 'react-router-dom';

import { useGetContactsQuery } from '@/api/v1';
import { ApplicationModal } from '@/components/organisms/ApplicationModal';
import { useCustomer } from '@/hooks/useCustomer';
import { useTranslator } from '@/hooks/useTranslator';
import { PaymentMethodCrypto } from '@/Screens/Dashboard/Contacts/PaymentMethodCrypto';
import { PaymentMethodFiat } from '@/Screens/Dashboard/Contacts/PaymentMethodFiat';
import { Contact, SelfContact } from '@/types/contacts';
import { FiatAccountType } from '@/types/fiatAccountType';
import { ModalFlowContext } from '@/types/modalFlowContext';
import { Page } from '@/types/page';

export const PaymentMethodModal: React.FC = (): React.ReactElement => {
  const tr = useTranslator();
  const customer = useCustomer();
  const modal = useOutletContext<ModalFlowContext>();

  const { contactId = null, accountType } = useParams();

  const onCreated = React.useCallback(
    (id: string): void => {
      modal.close({
        accountId: id,
      });
    },
    [modal],
  );

  const { data: contactsPage = Page.empty<Contact>() } = useGetContactsQuery(
    { beneficiaryId: customer.beneficiaryId, size: 1000 },
    { skip: !contactId },
  );
  const data = contactsPage.content;

  const contact = React.useMemo(
    (): Contact | SelfContact | undefined =>
      contactId
        ? data.find((contact: Contact): boolean => contact.id === contactId)
        : SelfContact.fromCustomer(customer),
    [customer, data, contactId],
  );

  const onClose = React.useCallback((): void => {
    modal.close();
  }, [modal]);

  const isCrypto = accountType === FiatAccountType.crypto;

  return (
    <ApplicationModal title={tr('PAYMENT_METHOD')} back=".." onClose={onClose}>
      <Box display="flex" flexDirection="column" gap={5}>
        {isCrypto ? (
          <PaymentMethodCrypto
            onCreated={onCreated}
            contact={contact}
            redirectPath={modal.closePath}
          />
        ) : (
          <PaymentMethodFiat
            onCreated={onCreated}
            contact={contact}
            redirectPath={modal.closePath}
          />
        )}
      </Box>
    </ApplicationModal>
  );
};
